import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTablePFSEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { useMemo, useState } from "react";
import { useGetAllAccountsQuery } from "store/queries/account";
import { useGetAthenaImportsQuery } from "store/queries/integration";
import { IAthenaImportLogItem } from "types/AthenaIntegration";

import { formatDateTime, userDisplayName } from "../../utils/hstImports";
import { AthenaImportResultsDialog } from "./AthenaImportResultsDialog";

const PAGE_SIZE = 25;
const TOOLTIP_CLASS = "athena-import-tooltip";

const whenBody = (i: IAthenaImportLogItem) => formatDateTime(i.createdAt);

const sourceBody = (i: IAthenaImportLogItem) =>
  i.source === "daily_sftp" ? (
    <Tag value="Daily SFTP" icon="pi pi-clock" severity="info" />
  ) : (
    <Tag
      value="Manual"
      icon="pi pi-user"
      style={{ background: "var(--gray-500)" }}
    />
  );

const accountBody = (i: IAthenaImportLogItem) =>
  i.accountName || <span className="text-500">Unknown</span>;

// A person for a manual upload; the SFTP partner for the daily sweep (older daily rows have no slug).
const importedByBody = (i: IAthenaImportLogItem) => {
  if (i.source === "daily_sftp") return i.partnerSlug || "Daily sweep";
  return userDisplayName(i.importedBy);
};

const statusBody = (i: IAthenaImportLogItem) => {
  switch (i.status) {
    case "completed":
      return <Tag value="Completed" severity="success" />;
    case "failed":
      return (
        <span
          className={TOOLTIP_CLASS}
          data-pr-tooltip={i.errorMessage || "No reason recorded"}
        >
          <Tag value="Failed" severity="danger" icon="pi pi-times" />
        </span>
      );
    case "processing":
      return <Tag value="Running" severity="info" icon="pi pi-spinner" />;
    default:
      return <Tag value="Queued" severity="warning" />;
  }
};

export function AthenaImportsSection() {
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [resultsFor, setResultsFor] = useState<IAthenaImportLogItem | null>(
    null
  );

  const { data: accounts } = useGetAllAccountsQuery(null);
  const accountOptions = useMemo(
    () =>
      [...(accounts?.data || [])]
        .map((account) => ({ label: account.name, value: account.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [accounts]
  );

  const { data, isFetching } = useGetAthenaImportsQuery({
    accountId,
    page,
    pageSize: PAGE_SIZE,
  });
  const list = data?.data;
  const items = list?.items || [];

  const onPage = (event: DataTablePFSEvent) => {
    setPage(Math.floor(event.first / event.rows) + 1);
  };

  const actionsBody = (i: IAthenaImportLogItem) => (
    <Button
      label="Results"
      className="p-button-text p-button-sm"
      disabled={i.status !== "completed"}
      onClick={() => setResultsFor(i)}
      data-testid={`athena-import-results-${i.id}`}
    />
  );

  return (
    <div className="flex flex-column gap-3">
      <Tooltip target={`.${TOOLTIP_CLASS}`} position="top" />
      <div className="flex flex-wrap align-items-center justify-content-between gap-3">
        <Dropdown
          value={accountId}
          options={accountOptions}
          onChange={(e) => {
            setAccountId(e.value || undefined);
            setPage(1);
          }}
          placeholder="All accounts"
          filter
          showClear
          style={{ minWidth: "280px" }}
          aria-label="Filter imports by account"
        />
        <small className="text-600">
          Manual uploads from the Estimates page and the daily SFTP import.
        </small>
      </div>

      <PageDataTable
        value={items}
        dataKey="id"
        lazy
        paginator
        first={(page - 1) * PAGE_SIZE}
        rows={PAGE_SIZE}
        totalRecords={list?.total || 0}
        onPage={onPage}
        loading={isFetching}
        emptyMessage="No Athena imports yet."
      >
        <Column header="When" body={whenBody} />
        <Column header="Source" body={sourceBody} />
        <Column header="Account" body={accountBody} />
        <Column header="Imported by" body={importedByBody} />
        <Column header="File" field="filename" />
        <Column header="Status" body={statusBody} />
        <Column header="Rows" field="totalRows" />
        <Column header="Groups" field="totalGroups" />
        <Column header="Created" field="successCount" />
        <Column header="Updated" field="updateCount" />
        <Column header="Errors" field="errorCount" />
        <Column header="Duplicates" field="duplicateCount" />
        <Column header="Unmapped payers" field="unmappedPayerCount" />
        <Column header="No facility" field="noFacilityCount" />
        <Column header="No CPT" field="noCptCount" />
        <Column header="Medicare supplement" field="medicareSupplementCount" />
        <Column header="" body={actionsBody} style={{ width: "7rem" }} />
      </PageDataTable>

      <AthenaImportResultsDialog
        item={resultsFor}
        onHide={() => setResultsFor(null)}
      />
    </div>
  );
}
